import {flow, getEnv, Instance, types} from 'mobx-state-tree';
import {AuctionSeriesViewModel} from '@joyrideautos/ui-models/src/presentation/auctionSeries/AuctionSeriesViewModel';
import {AuctionSeriesType} from '@joyrideautos/ui-models/src/types/auction/AuctionSeries';
import {when} from 'mobx';
import {PDFModalViewModel} from '@joyrideautos/ui-models/src/presentation/pdfModal/PDFModalViewModel';
import {TransientEventTypeEnum} from '@joyrideautos/auction-core/src/types/events/transient';
import BasePresenter from '@joyrideautos/web-common-components/src/models/BasePresenter';
import {AnalyticsFavoritesEventEnum} from '@joyrideautos/ui-services/src/services/AnalyticsService';

const ITEMS_PER_PAGE = 4;

const AuctionRegionPagePresenter = BasePresenter.named('AuctionRegionPagePresenter')
    .props({
        pdfModalViewModel: types.optional(PDFModalViewModel, {}),
        error: types.maybe(types.string),
    })
    .actions((self) => ({
        setError(error?: string) {
            self.error = error;
        },
    }))
    .views((self) => {
        return {
            get regionId() {
                const regionId = self.getRouteParamByName('regionId') || '';
                return regionId.toUpperCase();
            },
            get region() {
                return self.regionsStore.findRegion(this.regionId);
            },
            get regionName() {
                return this.region?.renderedDisplayName || '';
            },
            get stateId() {
                return this.region?.stateId || '';
            },
            get stateName() {
                return this.region?.state || '';
            },
            get upcomingAuctionSeries(): AuctionSeriesType[] {
                return self.auctionSeriesStore.getSeriesWithUpcomingAuctionsForRegion(this.regionId) || [];
            },
            get pastAuctionSeries(): AuctionSeriesType[] {
                const auctionSeries = self.auctionSeriesStore.getSeriesWithPastAuctionsForRegion(this.regionId) || [];
                return auctionSeries.filter((series) => !series.shouldHidePastAuctions);
            },
            getAuctionSeriesViewModel(series: AuctionSeriesType, forUpcomingAuctions: boolean) {
                // TODO: destroy this
                return AuctionSeriesViewModel.create(
                    {series: series.id, forUpcomingAuctions},
                    {...getEnv(self), itemsPerPage: ITEMS_PER_PAGE}
                );
            },
            get title() {
                return this.regionName;
            },
            get isFavorite() {
                return !!(this.regionId && self.favoritesStore.isFavoriteRegion(this.regionId));
            },
            get isLoading() {
                return !self.auctionSeriesStore.subscribeStatus.isReady || !self.auctionsStore.status.isReady;
            },
        };
    })
    .actions((self) => {
        return {
            toggleFavorite: flow(function* () {
                const region = self.region;
                if (region) {
                    const result = yield self.favoritesStore.toggleFavoriteRegion(region);
                    const eventType = result
                        ? TransientEventTypeEnum.FAVORITE_REGION_ADDED
                        : TransientEventTypeEnum.FAVORITE_REGION_REMOVED;
                    self.eventsStore.addTransientEvents({
                        type: eventType,
                        params: {itemTitle: region.renderedDisplayName},
                    });
                    // Firebase Analytics
                    const eventName = result
                        ? AnalyticsFavoritesEventEnum.ADD_TO_WISHLIST
                        : AnalyticsFavoritesEventEnum.REMOVE_FROM_WISHLIST;
                    yield self.rootStore.analyticsService.logFavoritesEvent(eventName, 'region', region.regionId);
                }
            }),
            afterCreate() {
                self.disposers.push(
                    when(
                        () => self.regionsStore.status.isReady,
                        () => {
                            if (!self.region) {
                                self.setError(`Missing region: '${self.regionId}'`);
                            } else {
                                self.setError();
                            }
                        }
                    )
                );
            },
        };
    });

export interface AuctionRegionPagePresenterType extends Instance<typeof AuctionRegionPagePresenter> {}

export default AuctionRegionPagePresenter;
